import { useEffect, useRef } from "react";
import "./hub-gate.css";

/**
 * The suite's shared sign-in screen.
 *
 * The card itself is `<suite-gate>` — defined by `gate.js`, served live by the
 * hub next to `chrome.js` — so palette, wording and the OAuth buttons are
 * edited centrally in that one file, never per application. This wrapper only
 * wires the suite's one session into the host's own auth state: a submit is
 * claimed here (`preventDefault`), routed through the hub (`onLogin` /
 * `onRegister`), failures render back on the card via `setAuthError`, and a
 * success flips the hook's user — which unmounts the gate.
 */

/** The suite gate element (defined by gate.js) — `setAuthError` is its own method. */
interface SuiteGateElement extends HTMLElement {
  setAuthError: (message: string) => void;
}

/** The hub origin: what the embedding pane hands over, else the register rule. */
function hubOrigin(): string {
  const fromPane = new URLSearchParams(window.location.search).get("host");
  if (fromPane) return fromPane.replace(/\/+$/, "");
  return `${window.location.protocol}//${window.location.hostname}:4040`;
}

let gateScriptStarted = false;

/** Load `gate.js` once per page; the element upgrades when it lands. */
function ensureGateScript(): void {
  if (gateScriptStarted) return;
  if (customElements.get("suite-gate")) {
    gateScriptStarted = true;
    return;
  }
  gateScriptStarted = true;
  const script = document.createElement("script");
  script.src = `${hubOrigin()}/gate.js`;
  script.defer = true;
  document.head.appendChild(script);
}

/** Reserved boot space: no layout jump while the session probe is in flight. */
export function HubBoot() {
  return (
    <div className="hg-boot" role="status">
      <span className="hg-boot-ring" />
      <span>Checking your Ortam session…</span>
    </div>
  );
}

interface AuthGateProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (name: string, email: string, password: string) => Promise<void>;
  unreachable: boolean;
  onRetry: () => void;
  /** The suite application id (e.g. "kor") — the OAuth flows name the app with it. */
  appId: string;
  appName: string;
}

export function AuthGate({ onLogin, onRegister, unreachable, onRetry, appId, appName }: AuthGateProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (unreachable) return;
    const host = hostRef.current;
    if (!host) return;
    ensureGateScript();

    const gate = document.createElement("suite-gate") as SuiteGateElement;
    gate.setAttribute("app", appId);
    gate.setAttribute("app-name", appName);
    host.appendChild(gate);

    const onCredentials = (e: Event) => {
      const detail = (
        e as CustomEvent<{ mode: "login" | "register"; email: string; password: string; name: string | null }>
      ).detail;
      e.preventDefault();
      const attempt =
        detail.mode === "login"
          ? onLogin(detail.email, detail.password)
          : onRegister(detail.name ?? "", detail.email, detail.password);
      attempt.catch((err: unknown) => gate.setAuthError((err as Error).message));
    };
    window.addEventListener("suite-gate:credentials", onCredentials);
    return () => {
      window.removeEventListener("suite-gate:credentials", onCredentials);
      gate.remove();
    };
  }, [unreachable, appId, appName, onLogin, onRegister]);

  if (unreachable) {
    return (
      <div className="hg-screen">
        <div className="hg-card">
          <div className="hg-brand">
            <div className="hg-badge">!</div>
            <h1 className="hg-title">Ortam is not answering</h1>
            <p className="hg-subtitle">
              The suite backend did not answer, so the session could not be checked. Start the
              suite (npm run suite:all in ortam) and try again.
            </p>
          </div>
          <div className="hg-form">
            <button type="button" className="hg-submit" onClick={onRetry}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <div ref={hostRef} />;
}
